/**
 * Geocoding utility functions using Google Maps API
 */

const GOOGLE_MAPS_API_KEY = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string) || '';

export interface GeocodeResult {
  latitude: number;
  longitude: number;
}

export interface AddressComponents {
  streetLine1?: string;
  streetLine2?: string;
  city?: string;
  province?: string;
  state?: string;
  postalCode?: string;
  zipCode?: string;
  country?: string;
}

/**
 * Build a full address string from address components
 */
function buildAddressString(components: AddressComponents): string {
  const parts = [
    components.streetLine1,
    components.streetLine2,
    components.city,
    components.province || components.state,
    components.postalCode || components.zipCode,
    components.country,
  ].filter(Boolean);

  return parts.join(', ');
}

/**
 * Geocode an address using Google Maps Geocoder API
 * Returns null if geocoding fails or API key is not configured
 */
export async function geocodeAddress(
  components: AddressComponents
): Promise<GeocodeResult | null> {
  // If API key is not configured, return null
  if (!GOOGLE_MAPS_API_KEY || !window.google) {
    console.warn('Google Maps API key not configured or Google Maps not loaded');
    return null;
  }

  const address = buildAddressString(components);

  // If address is empty, return null
  if (!address || address.trim().length === 0) {
    console.warn('Cannot geocode empty address');
    return null;
  }

  return new Promise((resolve) => {
    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ address }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        const location = results[0].geometry.location;
        resolve({
          latitude: location.lat(),
          longitude: location.lng(),
        });
      } else {
        console.warn(`Geocoding failed for address: ${address}. Status: ${status}`);
        resolve(null);
      }
    });
  });
}

/**
 * Check if Google Maps API is loaded and available
 */
export function isGoogleMapsLoaded(): boolean {
  return typeof window !== 'undefined' && 
         typeof window.google !== 'undefined' && 
         typeof window.google.maps !== 'undefined' &&
         typeof window.google.maps.Geocoder !== 'undefined';
}

